/**
 * Modal "Nuevo pedido de láminas" (réplica de `modalAgregarPedido` del SSR).
 *
 * Envuelve el `PedidoForm` (modo `create`) en un `Dialog` y, al enviar, llama a
 * `useCreatePedido` (mutación que invalida el tablero). Las sedes origen/destino
 * son las que resolvió la API para la sede activa (39<->41): se pasan al cuerpo y
 * se muestran en el formulario. Si la sede activa no participa (sin origen/destino),
 * el alta no es posible y se informa.
 *
 * Controlado por `open` del padre; presentacional + orquestación de un hook de
 * datos (no hace `fetch`). El error de la API se muestra dentro del formulario.
 */
import { useEffect } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui";

import { useCreatePedido } from "../hooks/usePedidoMutations";
import type { PedidoCreateBody } from "../types";
import type { PedidoFormValues } from "../schemas";
import { PedidoForm } from "./PedidoForm";

interface PedidoFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Sede origen/destino resueltas por la API para la sede activa (39<->41). */
  origenSedeId: number | null;
  destinoSedeId: number | null;
  origenNombre: string | null;
  destinoNombre: string | null;
}

/** Valores iniciales de un alta (todos vacíos, cantidad en texto). */
const EMPTY_VALUES: PedidoFormValues = {
  categoria: "",
  calibre: "",
  medida: "",
  tubo: "",
  descripcion: "",
  cantidad: "",
};

export function PedidoFormModal({
  open,
  onClose,
  origenSedeId,
  destinoSedeId,
  origenNombre,
  destinoNombre,
}: PedidoFormModalProps) {
  const create = useCreatePedido();
  const canCreate = origenSedeId !== null && destinoSedeId !== null;

  // Al abrir, limpia el estado de la mutación (evita arrastrar un error previo).
  useEffect(() => {
    if (open) {
      create.reset();
    }
    // `create` es estable por render de TanStack Query; solo nos interesa `open`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleSubmit(values: PedidoFormValues) {
    if (!canCreate) {
      return;
    }
    const body: PedidoCreateBody = {
      tipo_pedido: "unico",
      origen_sede_id: origenSedeId,
      destino_sede_id: destinoSedeId,
      categoria: values.categoria.trim(),
      calibre: (values.calibre ?? "").trim(),
      medida: (values.medida ?? "").trim(),
      tubo: (values.tubo ?? "").trim(),
      descripcion: (values.descripcion ?? "").trim(),
      cantidad: values.cantidad.trim(),
    };
    create.mutate(body, { onSuccess: () => onClose() });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogTitle>Nuevo pedido de láminas</DialogTitle>
        <DialogDescription>
          Completa los datos del material que necesitas.
        </DialogDescription>

        <div className="mt-4">
          {canCreate ? (
            <PedidoForm
              initialValues={EMPTY_VALUES}
              origenNombre={origenNombre}
              destinoNombre={destinoNombre}
              isSubmitting={create.isPending}
              apiError={create.isError ? create.error : null}
              onSubmit={handleSubmit}
              submitLabel="Enviar pedido"
              onCancel={onClose}
            />
          ) : (
            <p className="text-sm text-amber-700" role="alert">
              Solo se pueden crear pedidos desde las sedes 39 o 41.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
